import { DEFAULT_VERTICAL_POSITION_THRESHOLD_DEGREES } from "./combinedOrientation";

export { DEFAULT_VERTICAL_POSITION_THRESHOLD_DEGREES };

export const LEGACY_VIEWER_PREFERENCES_KEY = "neoma.signalViewer.preferences.v1";
export const V2_VIEWER_PREFERENCES_KEY = "neoma.signalViewer.preferences.v2";
export const VIEWER_PREFERENCES_KEY = "neoma.signalViewer.preferences.v3";
export const DEFAULT_HYPNOGRAM_HEIGHT = 160;
export const MIN_HYPNOGRAM_HEIGHT = 100;
export const MAX_HYPNOGRAM_HEIGHT = 600;
export const DEFAULT_MODEL_COLOR = "#28b9aa";
export const DEFAULT_MODEL_OPACITY = 0.68;
export const DEFAULT_GRAVITY_ARROW_COLOR = "#63B8C4";
export const DEFAULT_GRAVITY_LEGEND_TEXT = "Gravity ↓";
export const MAX_GRAVITY_LEGEND_LENGTH = 80;

export type OrientationModelId = "head" | "torso";
export interface ModelAppearance { color: string; opacity: number }
export interface VisibilityLabel { modality: string; channel?: string; defaultColour?:string }
export const MAX_VISIBILITY_OVERRIDES = 128;

export const DEFAULT_COMBINED_APPEARANCE = {
  head: { color: "#28b9aa", opacity: 1 },
  torso: { color: "#688bc4", opacity: 1 },
};

export interface ViewerPreferences {
  labelWidthMode: "compact" | "expanded";
  compactLabelWidth: number;
  expandedLabelWidth: number;
  distributionUnits: "seconds" | "minutes";
  channelDisplay: Record<string, import("./channelDisplay").ChannelDisplayOverride>;
  windowVisibility: Record<string, import("./channelDisplay").WindowVisibility>;
  channelOrder: Record<string, string[]>;
  showBulkChannelHandoff: boolean;
  showSleepStagingInformation: boolean;
  showOrientationPanel: boolean;
  showOrientationExplanation: boolean;
  showRecordingMetrics: boolean;
  trackVisibility: Record<string, boolean>;
  showPositionDistribution: boolean;
  angularSmoothing: import("./positionDistribution").AngularSmoothing;
  distributionTimeMode: "cumulative" | "interval";
  distributionColourMode: "sensor" | "spo2";
  combinedOrientation: boolean;
  combinedAppearance: Record<OrientationModelId, ModelAppearance>;
  showHypnogram: boolean;
  hypnogramHeight: number;
  separateMultichannelSignals: boolean;
  showSelectedTotalTime: boolean;
  alignTimelineToSignalViewport: boolean;
  highlightActiveSleepStage: boolean;
  cursorFollowsMouse: boolean;
  link3dViews: boolean;
  suppressRelativeHeading: boolean;
  verticalPositionThresholdDegrees: number;
  showGravityDirection: boolean;
  gravityArrowColor: string;
  showGravityLegend: boolean;
  gravityLegendText: string;
  modelAppearance: Record<OrientationModelId, ModelAppearance>;
  channelVisibility: Record<string, boolean>;
  visibilityLabels: Record<string, VisibilityLabel>;
}

const sharedBooleanNames = [
  "separateMultichannelSignals",
  "showSelectedTotalTime",
  "alignTimelineToSignalViewport",
  "highlightActiveSleepStage",
  "cursorFollowsMouse",
  "link3dViews",
] as const;

export const DEFAULT_VIEWER_PREFERENCES: Readonly<ViewerPreferences> = Object.freeze({
  labelWidthMode: "compact", compactLabelWidth: 96, expandedLabelWidth: 180,
  distributionUnits: "seconds", channelDisplay: {}, windowVisibility: {}, channelOrder: {},
  showBulkChannelHandoff: false,
  showSleepStagingInformation: false,
  showOrientationPanel: true,
  showOrientationExplanation: false,
  showRecordingMetrics: true,
  trackVisibility: {},
  showPositionDistribution: false,
  angularSmoothing: 0,
  distributionTimeMode: "cumulative",
  distributionColourMode: "sensor",
  combinedOrientation: true,
  combinedAppearance: DEFAULT_COMBINED_APPEARANCE,
  showHypnogram: false,
  hypnogramHeight: DEFAULT_HYPNOGRAM_HEIGHT,
  separateMultichannelSignals: true,
  showSelectedTotalTime: true,
  alignTimelineToSignalViewport: true,
  highlightActiveSleepStage: false,
  cursorFollowsMouse: false,
  link3dViews: true,
  suppressRelativeHeading: false,
  verticalPositionThresholdDegrees: DEFAULT_VERTICAL_POSITION_THRESHOLD_DEGREES,
  showGravityDirection: true,
  gravityArrowColor: DEFAULT_GRAVITY_ARROW_COLOR,
  showGravityLegend: true,
  gravityLegendText: DEFAULT_GRAVITY_LEGEND_TEXT,
  modelAppearance: {
    head: { color: DEFAULT_MODEL_COLOR, opacity: DEFAULT_MODEL_OPACITY },
    torso: { color: DEFAULT_COMBINED_APPEARANCE.torso.color, opacity: DEFAULT_MODEL_OPACITY },
  },
  channelVisibility: {},
  visibilityLabels: {},
});

type StoredViewerPreferences = ViewerPreferences & { version: 3 };
type StoredV2ViewerPreferences = {
  version: 2;
  tiltOnlyOrientation: boolean;
  showWorldDown: boolean;
  modelAppearance: Record<OrientationModelId, ModelAppearance>;
  channelVisibility: Record<string, boolean>;
} & Record<(typeof sharedBooleanNames)[number], boolean>;

function defaults(): ViewerPreferences {
  return {
    ...DEFAULT_VIEWER_PREFERENCES,
    combinedAppearance: { head: { ...DEFAULT_COMBINED_APPEARANCE.head }, torso: { ...DEFAULT_COMBINED_APPEARANCE.torso } },
    modelAppearance: {
      head: { ...DEFAULT_VIEWER_PREFERENCES.modelAppearance.head },
      torso: { ...DEFAULT_VIEWER_PREFERENCES.modelAppearance.torso },
    },
    channelDisplay: {}, windowVisibility: {}, channelOrder: {},
    trackVisibility: {},
    channelVisibility: {},
    visibilityLabels: {},
  };
}

export function viewerPreferencesKey(identity: string): string {
  if (!identity.trim() || identity === "anonymous") throw new TypeError("authenticated preference identity is required");
  return `${VIEWER_PREFERENCES_KEY}:${encodeURIComponent(identity)}`;
}

export function v2ViewerPreferencesKey(identity: string): string {
  if (!identity.trim() || identity === "anonymous") throw new TypeError("authenticated preference identity is required");
  return `${V2_VIEWER_PREFERENCES_KEY}:${encodeURIComponent(identity)}`;
}

export function validModelColor(value: unknown): value is string {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
}

export function clampModelOpacity(value: number): number {
  return Math.max(0.1, Math.min(1, Number.isFinite(value) ? value : DEFAULT_MODEL_OPACITY));
}

/** Upright/inverted band in degrees of crown-axis inclination from vertical, clamped to 0–90. */
export function clampVerticalPositionThresholdDegrees(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.min(90, value))
    : DEFAULT_VERTICAL_POSITION_THRESHOLD_DEGREES;
}

export function normalizeHypnogramHeight(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.round(Math.max(MIN_HYPNOGRAM_HEIGHT, Math.min(MAX_HYPNOGRAM_HEIGHT, value)))
    : DEFAULT_HYPNOGRAM_HEIGHT;
}

export function normalizeGravityLegendText(value: string): string {
  return value.slice(0, MAX_GRAVITY_LEGEND_LENGTH);
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
function boundedVisibility(value: unknown): Record<string, boolean> {
  return Object.fromEntries(Object.entries(object(value))
    .filter((entry): entry is [string, boolean] => entry[0].length > 0 && entry[0].length <= 240 && typeof entry[1] === "boolean")
    .slice(-MAX_VISIBILITY_OVERRIDES));
}
function appearance(value: unknown, fallback: ModelAppearance): ModelAppearance {
  const v = object(value);
  return {color: validModelColor(v.color) ? v.color : fallback.color,
    opacity: typeof v.opacity === "number" && Number.isFinite(v.opacity) && v.opacity >= 0.1 && v.opacity <= 1 ? v.opacity : fallback.opacity};
}
const booleanNames = [
  ...sharedBooleanNames, "showBulkChannelHandoff", "showSleepStagingInformation", "showOrientationExplanation", "showOrientationPanel",
  "showRecordingMetrics", "showPositionDistribution", "combinedOrientation", "showHypnogram", "suppressRelativeHeading",
  "showGravityDirection", "showGravityLegend",
] as const;

/** Per-field validation preserves unrelated choices in partially damaged/older v3 records. */
export function normalizeViewerPreferences(value: unknown): ViewerPreferences {
  const v = object(value), result = defaults();
  for (const key of booleanNames) if (typeof v[key] === "boolean") result[key] = v[key];
  if(v.labelWidthMode === "compact" || v.labelWidthMode === "expanded")result.labelWidthMode=v.labelWidthMode;
  const width=(x:unknown,fallback:number)=>typeof x === "number" && Number.isFinite(x)?Math.round(Math.max(64,Math.min(480,x))):fallback;
  result.hypnogramHeight=normalizeHypnogramHeight(v.hypnogramHeight);
  result.compactLabelWidth=width(v.compactLabelWidth,96);
  result.expandedLabelWidth=Math.max(result.compactLabelWidth,width(v.expandedLabelWidth,180));
  if(v.distributionUnits === "minutes")result.distributionUnits="minutes";
  const entries=(x:unknown)=>Object.entries(object(x)).filter(([k])=>k.length>0&&k.length<=240).slice(-256);
  result.channelDisplay=Object.fromEntries(entries(v.channelDisplay).map(([key,value])=>{
    const saved=object(value), display:import("./channelDisplay").ChannelDisplayOverride={};
    for(const name of ["shortName","longName"] as const)if(typeof saved[name] === "string" && saved[name].trim())display[name]=saved[name].slice(0,name === "shortName"?40:120);
    if(validModelColor(saved.colour))display.colour=saved.colour;
    if(saved.scaleMode === "auto" || saved.scaleMode === "fixed")display.scaleMode=saved.scaleMode;
    if(typeof saved.span === "number" && Number.isFinite(saved.span) && saved.span>=1e-9 && saved.span<=1e12)display.span=saved.span;
    if(display.scaleMode === "fixed" && !display.span)display.scaleMode="auto";
    if(saved.stageStyle === "bands" || saved.stageStyle === "hypnogram")display.stageStyle=saved.stageStyle;
    if(["quadrants","angle","both"].includes(saved.positionRepresentation as string))display.positionRepresentation=saved.positionRepresentation as typeof display.positionRepresentation;
    if(saved.presentationStyle === "bands" || saved.presentationStyle === "trace")display.presentationStyle=saved.presentationStyle;
    if(saved.batteryRepresentation === "numeric" || saved.batteryRepresentation === "categories")display.batteryRepresentation=saved.batteryRepresentation;
    return [key,display];
  }));
  result.windowVisibility=Object.fromEntries(entries(v.windowVisibility).map(([key,value])=>[key,
    Object.fromEntries(Object.entries(object(value)).filter(([w,x])=>["full","15","30","60"].includes(w)&&typeof x === "boolean"))]));
  result.channelOrder=Object.fromEntries(entries(v.channelOrder).slice(-32).map(([key,value])=>[key,
    Array.isArray(value)?Array.from(new Set(value.filter((k):k is string=>typeof k === "string" && k.length>0 && k.length<=240))).slice(0,256):[]]));
  result.trackVisibility = boundedVisibility(v.trackVisibility);
  result.channelVisibility = boundedVisibility(v.channelVisibility);
  result.angularSmoothing = [0, 5, 15, 30].includes(v.angularSmoothing as number) ? v.angularSmoothing as ViewerPreferences["angularSmoothing"] : result.angularSmoothing;
  if (v.distributionTimeMode === "interval" || v.distributionTimeMode === "cumulative") result.distributionTimeMode = v.distributionTimeMode;
  if (v.distributionColourMode === "spo2" || v.distributionColourMode === "sensor") result.distributionColourMode = v.distributionColourMode;
  result.verticalPositionThresholdDegrees = clampVerticalPositionThresholdDegrees(v.verticalPositionThresholdDegrees);
  if (validModelColor(v.gravityArrowColor)) result.gravityArrowColor = v.gravityArrowColor;
  if (typeof v.gravityLegendText === "string") result.gravityLegendText = v.gravityLegendText === "World down ↓"
    ? DEFAULT_GRAVITY_LEGEND_TEXT : normalizeGravityLegendText(v.gravityLegendText);
  for (const key of ["modelAppearance", "combinedAppearance"] as const) {
    const saved = object(v[key]);
    for (const model of ["head", "torso"] as const) result[key][model] = appearance(saved[model], result[key][model]);
  }
  const labels = object(v.visibilityLabels);
  result.visibilityLabels = Object.fromEntries(Array.from(new Set([...Object.keys(result.channelVisibility), ...Object.keys(result.trackVisibility), ...Object.keys(result.windowVisibility), ...Object.keys(result.channelDisplay)])).flatMap(key=>{
    const label=object(labels[key]);
    return typeof label.modality === "string" && label.modality.trim() ? [[key,{
      modality:label.modality.slice(0,160), ...(validModelColor(label.defaultColour)?{defaultColour:label.defaultColour}:{}), ...(typeof label.channel === "string" && label.channel.trim()?{channel:label.channel.slice(0,160)}:{}),
    }]] : [];
  }));
  return result;
}
export function parseViewerPreferences(raw: string | null): ViewerPreferences {
  if (raw == null) return defaults();
  try {
    const v: unknown = JSON.parse(raw);
    return object(v).version === 3 ? normalizeViewerPreferences(v) : defaults();
  } catch { return defaults(); }
}
export function serializeViewerPreferences(preferences: ViewerPreferences): string {
  return JSON.stringify({version: 3, ...normalizeViewerPreferences(preferences)});
}

function parseV2(raw: string | null): StoredV2ViewerPreferences | null {
  if (raw == null) return null;
  try {
    const v = object(JSON.parse(raw));
    return v.version === 2 ? v as unknown as StoredV2ViewerPreferences : null;
  } catch { return null; }
}
function migrateV2(value: StoredV2ViewerPreferences): ViewerPreferences {
  return normalizeViewerPreferences({...value, suppressRelativeHeading: false,
    showGravityDirection: typeof value.showWorldDown === "boolean" ? value.showWorldDown : true});
}
function parseLegacy(raw: string | null): Partial<ViewerPreferences> | null {
  if (raw == null) return null;
  try {
    const v = object(JSON.parse(raw));
    if (v.version !== 1) return null;
    return Object.fromEntries(sharedBooleanNames.filter(name => typeof v[name] === "boolean").map(name => [name, v[name]]));
  } catch { return null; }
}

function availableStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

type PreferenceStorage = Pick<Storage, "getItem" | "setItem">;

export function loadViewerPreferences(identity: string, storage?: PreferenceStorage | null): ViewerPreferences {
  const source = storage === undefined ? availableStorage() : storage;
  if (!source) return defaults();
  try {
    const key = viewerPreferencesKey(identity);
    const current = source.getItem(key);
    if (current != null) return parseViewerPreferences(current);
    const previous = parseV2(source.getItem(v2ViewerPreferencesKey(identity)));
    const legacy = previous ? null : parseLegacy(source.getItem(LEGACY_VIEWER_PREFERENCES_KEY));
    const migrated = previous ? migrateV2(previous) : legacy ? { ...defaults(), ...legacy } : defaults();
    try { source.setItem(key, serializeViewerPreferences(migrated)); } catch { /* Retain valid values even if saving is unavailable. */ }
    return migrated;
  } catch {
    return defaults();
  }
}

export function saveViewerPreferences(
  identity: string,
  preferences: ViewerPreferences,
  storage?: Pick<Storage, "setItem"> | null,
): boolean {
  const destination = storage === undefined ? availableStorage() : storage;
  if (!destination) return false;
  try {
    destination.setItem(viewerPreferencesKey(identity), serializeViewerPreferences(preferences));
    return true;
  } catch {
    // Viewing remains usable when storage is unavailable or full.
    return false;
  }
}

export function channelVisibilityKey(
  profileId: string,
  profileVersion: string,
  modalityId: string,
  channelId: string,
): string {
  return [profileId, profileVersion, modalityId, channelId].map(encodeURIComponent).join("|");
}

export function defaultChannelVisible(modalityId: string, modalityLabel: string): boolean {
  return !/(^|[._\s-])(acc|accelerometer|gyr|gyro|gyroscope)([._\s-]|$)/i.test(`${modalityId} ${modalityLabel}`);
}

export function reconcileChannelVisibility(
  overrides: Record<string, boolean>,
  currentInventory: ReadonlySet<string>,
  maximumUnmatched = 32,
): Record<string, boolean> {
  const entries = Object.entries(overrides);
  const matched = entries.filter(([key]) => currentInventory.has(key));
  const unmatched = entries.filter(([key]) => !currentInventory.has(key)).slice(-maximumUnmatched);
  return Object.fromEntries([...unmatched, ...matched].slice(-128));
}

/** Separate namespace: row visibility never fabricates numerical channel IDs. */
export function trackVisibilityKey(profileId: string, profileVersion: string, modalityId: string): string {
  return ["track", profileId, profileVersion, modalityId].map(encodeURIComponent).join("|");
}

export type PreferenceResetScope = "reusable" | "visibility" | "all";
export function resetViewerPreferences(current: ViewerPreferences, scope: PreferenceResetScope): ViewerPreferences {
  if (scope === "all") return defaults();
  if (scope === "visibility") return {...current, channelVisibility:{}, trackVisibility:{}, windowVisibility:{}};
  return {...defaults(), channelDisplay:{...current.channelDisplay}, windowVisibility:{...current.windowVisibility}, channelOrder:{...current.channelOrder}, channelVisibility:{...current.channelVisibility}, trackVisibility:{...current.trackVisibility},
    visibilityLabels:{...current.visibilityLabels}};
}

/** IDs are decoded as IDs, never invented channel names; labels come from encountered authorized inventories. */
export function describeVisibilityOverride(key: string, kind: "channel" | "track") {
  try {
    const parts = key.split("|").map(decodeURIComponent);
    if (parts.length !== 4 || kind === "track" && parts[0] !== "track") return null;
    return kind === "track" ? {profile:parts[1],version:parts[2],modality:parts[3],channel:null}
      : {profile:parts[0],version:parts[1],modality:parts[2],channel:parts[3]};
  } catch { return null; }
}
